import type { ChainedCommands, Editor } from "@tiptap/react";
import {
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  ListTodo,
  Minus,
  Type,
  Table,
  TextQuote,
  type LucideIcon,
} from "lucide-react";

export type BlockType = {
  id: string;
  label: string;
  icon: LucideIcon;
  /** Markdown shortcut that produces the same block, shown as a hint. */
  shortcut?: string;
  keywords?: string[];
  apply: (chain: ChainedCommands) => ChainedCommands;
  isActive?: (editor: Editor) => boolean;
};

/** Blocks the selection bubble can turn the current block into. */
export const TEXT_BLOCK_TYPES: BlockType[] = [
  {
    id: "text",
    label: "Text",
    icon: Type,
    keywords: ["paragraph", "plain", "p", "body", "normal"],
    apply: (chain) => chain.clearNodes(),
    isActive: (editor) =>
      editor.isActive("paragraph") &&
      !editor.isActive("bulletList") &&
      !editor.isActive("orderedList") &&
      !editor.isActive("taskList") &&
      !editor.isActive("blockquote"),
  },
  ...([1, 2, 3] as const).map(
    (level): BlockType => ({
      id: `h${level}`,
      label: `Heading ${level}`,
      icon: [Heading1, Heading2, Heading3][level - 1],
      shortcut: `${"#".repeat(level)} `,
      keywords: [`h${level}`, "heading", "title", "header", "headline", "section"],
      apply: (chain) => chain.clearNodes().setHeading({ level }),
      isActive: (editor) => editor.isActive("heading", { level }),
    }),
  ),
  {
    id: "bulletList",
    label: "Bulleted list",
    icon: List,
    shortcut: "- ",
    keywords: ["bullet", "unordered", "ul", "list", "dash", "points"],
    apply: (chain) => chain.clearNodes().toggleBulletList(),
    isActive: (editor) => editor.isActive("bulletList"),
  },
  {
    id: "orderedList",
    label: "Numbered list",
    icon: ListOrdered,
    shortcut: "1. ",
    keywords: ["ordered", "ol", "list", "numbers", "enumerate", "steps"],
    apply: (chain) => chain.clearNodes().toggleOrderedList(),
    isActive: (editor) => editor.isActive("orderedList"),
  },
  {
    id: "taskList",
    label: "Checklist",
    icon: ListTodo,
    shortcut: "[] ",
    keywords: ["todo", "to-do", "task", "checkbox", "check", "list"],
    apply: (chain) => chain.clearNodes().toggleTaskList(),
    isActive: (editor) => editor.isActive("taskList"),
  },
  {
    id: "quote",
    label: "Quote",
    icon: TextQuote,
    shortcut: "> ",
    keywords: ["blockquote", "citation", "cite", "callout"],
    apply: (chain) => chain.clearNodes().toggleBlockquote(),
    isActive: (editor) => editor.isActive("blockquote"),
  },
  {
    id: "codeBlock",
    label: "Code block",
    icon: Code,
    shortcut: "```",
    keywords: ["code", "snippet", "pre", "monospace", "fence"],
    apply: (chain) => chain.clearNodes().toggleCodeBlock(),
    isActive: (editor) => editor.isActive("codeBlock"),
  },
];

/** Everything the `/` menu can insert. */
export const SLASH_BLOCK_TYPES: BlockType[] = [
  ...TEXT_BLOCK_TYPES,
  {
    id: "divider",
    label: "Divider",
    icon: Minus,
    shortcut: "---",
    keywords: ["hr", "rule", "separator", "line", "break"],
    apply: (chain) => chain.setHorizontalRule(),
  },
  {
    id: "table",
    label: "Table",
    icon: Table,
    keywords: ["grid", "rows", "columns", "spreadsheet"],
    apply: (chain) => chain.insertTable({ rows: 3, cols: 3, withHeaderRow: true }),
  },
];
